import { useState } from 'react';
import { createPortal } from 'react-dom';
import { shuffle } from 'lodash';

import { useContextEffect, useContextMemo, useParams, useStableCallback } from '../../../hooks';
import { useAssignmentContext } from '../context/AssignmentContext';
import { Button, FullPageLoader } from '../../shared';
import { Card } from '../../ui/card';
import { EndTest } from '../AssignmentActions/EndTest';
import { QuestionTypeBadge } from '../QuestionTypeBadge';
import ReadingComprehensionLayout from './ReadingComprehensionLayout';
import McqQuestionBody from './McqQuestionBody';
import FIBQuestionBody from './FIBQuestionBody';
import SubjectiveQuestionBody from './SubjectiveQuestionBody';
import CodeQuestionBody from './CodeQuestionBody';
import { QUESTION_TYPES } from '../../../constants';
import FileUploadQuestionBody from './FileUploadQuestionBody';
import AudioRecorderQuestion from './AudioRecorderQuestion';
import VideoRecorderQuestion from './VideoRecorderQuestion';
import ChoiceGroupBanner from './ChoiceGroupBanner';
import PassageChoiceNote from './PassageChoiceNote';
import {
  getChoiceGroupForPassage,
  getChoiceGroupForQuestion,
} from '../../../utils/choiceGroups';

const AssignmentQuestion = (props) => {
  const {
    question = {},
    questionIndex,
    onSaveAndNext,
    isLastQuestion,
    onSave,
    onSkip,
    questionResponse = {},
    isSubmittingResponse,
    isLoading,
    pauseStrictMode,
    isStrictModeActive,
    resumeStrictMode,
    rcContext,
    assignmentData,
    readingComprehensionQuestionRef
  } = props;
  const { isMediaBusy, mobileActionsHost } = useAssignmentContext();
  const isBusy = isLoading || isMediaBusy;

  const { id, type } = question;
  const { questionId } = useParams();
  const [answers, setAnswers] = useState(new Map());

  const updateAnswer = useStableCallback((qId, answer) => {
    setAnswers((prev) => {
      const newAnswers = new Map(prev);
      const answerObj = {};
      switch (type) {
        case QUESTION_TYPES.MCQ:
        case QUESTION_TYPES.SUBJECTIVE:
        case QUESTION_TYPES.CODE:
          answerObj.answer = answer;
          break;
        case QUESTION_TYPES.FIB:
          answerObj.answers = answer;
          break;
        case QUESTION_TYPES.FILE_UPLOAD:
        case QUESTION_TYPES.AUDIO:
        case QUESTION_TYPES.VIDEO:
          answerObj.uploadedFile = answer;
          break;
      }
      newAnswers.set(qId, answerObj);
      return newAnswers;
    });
  });

  const onUpdateAndSaveAnswer = useStableCallback((qId, answer) => {
    updateAnswer(qId, answer);
    switch (type) {
      case QUESTION_TYPES.MCQ:
      case QUESTION_TYPES.SUBJECTIVE:
      case QUESTION_TYPES.CODE:
        return onSave({ questionResponse, answer, type });
      case QUESTION_TYPES.FIB:
        return onSave({ questionResponse, answers: answer, type });
      case QUESTION_TYPES.FILE_UPLOAD:
      case QUESTION_TYPES.AUDIO:
      case QUESTION_TYPES.VIDEO:
        return onSave({ questionResponse, uploadedFile: answer, type });
      default:
        return undefined;
    }
  });

  const onSkipQuestion = useStableCallback(() => {
    updateAnswer(questionId, null);
    onSkip(questionResponse);
  });

  const onQuestionSaveAndNext = useStableCallback((shouldEndTest = false) => {
    const ansObj = answers.get(questionId) || {};
    onSaveAndNext({ ...ansObj, questionResponse, shouldEndTest, type });
  });

  useContextEffect({
    deps: [],
    cb: (ctx) => {
      ctx.updateAnswer(ctx.questionId, ctx.questionResponse.answer || ctx.questionResponse.answers);
    },
    ctx: { questionId, updateAnswer, questionResponse }
  });

  const options = useContextMemo({
    deps: [questionId, isLoading],
    ctx: { options: question.options, isLoading },
    cb: (ctx) => {
      if (ctx.isLoading) {
        return [];
      }
      return shuffle(ctx.options);
    }
  });

  let QuestionComponent = '';
  if (type === QUESTION_TYPES.MCQ) {
    QuestionComponent = McqQuestionBody;
  } else if (type === QUESTION_TYPES.FIB) {
    QuestionComponent = FIBQuestionBody;
  } else if (type === QUESTION_TYPES.SUBJECTIVE) {
    QuestionComponent = SubjectiveQuestionBody;
  } else if (type === QUESTION_TYPES.FILE_UPLOAD) {
    QuestionComponent = FileUploadQuestionBody;
  } else if (type === QUESTION_TYPES.CODE) {
    QuestionComponent = CodeQuestionBody;
  } else if (type === QUESTION_TYPES.AUDIO) {
    QuestionComponent = AudioRecorderQuestion;
  } else if (type === QUESTION_TYPES.VIDEO) {
    QuestionComponent = VideoRecorderQuestion;
  }

  if (isLoading) {
    return <FullPageLoader />;
  }

  const rcRef = readingComprehensionQuestionRef || question.readingComprehensionQuestionRef;
  const hasRcLayout = Boolean(rcContext && rcRef);

  const actionButtons = (
    <div className="flex w-full gap-3">
      {!isLastQuestion ? (
        <>
          <Button
            buttonType="secondary"
            isLoading={isBusy}
            onClick={onSkipQuestion}
            isDisabled={isSubmittingResponse || isBusy}
            className="min-w-0 flex-1 basis-0"
          >
            Skip Question
          </Button>
          <Button
            isLoading={isBusy || isSubmittingResponse}
            onClick={() => onQuestionSaveAndNext()}
            isDisabled={isSubmittingResponse || isBusy}
            className="min-w-0 flex-1 basis-0"
          >
            {isMediaBusy ? 'Uploading…' : 'Save and Next'}
          </Button>
        </>
      ) : (
        <>
          <Button
            buttonType="secondary"
            isLoading={isBusy}
            onClick={onSkipQuestion}
            isDisabled={isSubmittingResponse || isBusy}
            className="min-w-0 flex-1 basis-0"
          >
            Skip Question
          </Button>
          <EndTest
            buttonText={isMediaBusy ? 'Uploading…' : 'Save and End Test'}
            isSubmittingResponse={isSubmittingResponse || isBusy}
            onEndTest={() => onQuestionSaveAndNext(true)}
            assignment={assignmentData?.assignment}
            studentResponse={assignmentData?.studentResponse}
            className="min-w-0 flex-1 basis-0 bg-red-600 text-white hover:bg-red-700"
          />
        </>
      )}
    </div>
  );

  // A choice among these questions, and separately whether the passage they belong to is
  // itself one of several alternatives. Only the first is about this question.
  const choiceGroup = getChoiceGroupForQuestion(assignmentData?.assignment, id);
  const passageGroup = getChoiceGroupForPassage(assignmentData?.assignment, id);

  // The passage note comes first: it is about the whole block, the banner only about a choice
  // inside it.
  const questionCard = (
    <Card className="border border-border/50 p-4 shadow-sm md:p-6">
      <PassageChoiceNote group={passageGroup} />
      <ChoiceGroupBanner
        group={choiceGroup}
        studentResponse={assignmentData?.studentResponse}
        assignment={assignmentData?.assignment}
      />

      <div className="mb-4">
        <QuestionTypeBadge type={type} />
      </div>

      <QuestionComponent
        {...props}
        {...question}
        {...questionResponse}
        pauseStrictMode={pauseStrictMode}
        isStrictModeActive={isStrictModeActive}
        resumeStrictMode={resumeStrictMode}
        options={options}
        updateAnswer={updateAnswer}
        updateAndSaveAnswer={onUpdateAndSaveAnswer}
        readingComprehensionQuestionRef={hasRcLayout ? null : rcRef}
        key={questionId}
      />
    </Card>
  );

  const pinnedActions =
    mobileActionsHost &&
    createPortal(
      <div className="border-t border-border/60 bg-card px-3 py-3 md:px-4 md:pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
        <div className="px-4 md:px-6">{actionButtons}</div>
      </div>,
      mobileActionsHost
    );

  return id ? (
    <div className="w-full" key={questionId}>
      {hasRcLayout ? (
        <ReadingComprehensionLayout
          readingComprehensionQuestionRef={rcRef}
          rcContext={rcContext}
          assignmentData={assignmentData}
        >
          {questionCard}
        </ReadingComprehensionLayout>
      ) : (
        <div className="px-3 pb-3 md:px-4 md:pb-4">{questionCard}</div>
      )}
      {pinnedActions}
    </div>
  ) : (
    ''
  );
};

export default AssignmentQuestion;
